import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Redirect } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import { useModal } from '@famly/mf_modals-dialogs_context';
import { useResilientQuery } from '@famly/stat_apollo-query';
import { Text } from '@famly/mf_data-display_text';
import { Stack, createStack } from '@famly/mf_layout_stack';
import { createBox } from '@famly/mf_layout_box';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { accessToken, getSiteId, isQrSignin } from 'signin-app/login/selectors';
import translate from 'signin-app/helpers/translate';
import { fetchOverview } from 'signin-app/groups/actions';
import { institutionTitle, didLoadInstitution } from 'signin-app/groups/selectors';
import { media } from 'web-app/styleguide/utils';
import { getPillState, PIN, PINButtonContainer, StyledBackspace, StyledPINButton, } from 'web-app/react/components/pin/pin';
import ProgressPills, { ProgressPill } from 'web-app/react/components/progress-pills/progress-pills';
import { SettingsDropdown } from 'signin-app/components/settings-dropdown';
import { useWhitelabelConfiguration } from 'signin-app/whitelabel/use-whitelabel-configuration';
import { WhitelabelLogo } from 'signin-app/whitelabel/whitelabel-logo';
import { useBindDispatch } from 'web-app/react/hooks/use-bind-dispatch';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import { saveIsQRSigninAvailable, saveSiteId } from 'signin-app/api/helpers/local-storage';
import { useTransformQuery } from '@famly/vet_apollo-utils_use-transform-query';
import * as LoginQueries from 'signin-app/login/queries';
import * as LoginActions from 'signin-app/login/actions';
import { QRCode, TextContainer } from 'signin-app/qr/qr-screen';
import * as Breakpoints from 'web-app/styleguide/breakpoint-helpers';
import RoutesMap from 'signin-app/routes/routes-map';
import { HeaderLogoContainer } from 'signin-app/components/containers';
import { Initials } from 'signin-app/components/initials';
import { useCustomWhitelabelColor } from 'signin-app/components/hooks/use-custom-whitelabel-color';
import { GuestEntry } from 'signin-app/guest/guest-entry';
import { useListenForBiometricEvents } from './use-listen-for-biometric-events';
import * as Queries from './queries';
import { redirectUrl, pinHolder, pinState } from './selectors';
import { setPinValue, pinLogin } from './actions';
const loadInfoModal = () => import('signin-app/components/info-modal');
const StyledContainer = styled(createStack({})) `
    /** Needed to add scroll to accommodate PIN + QR on smaller screens and in case there is longer text in DE */
    max-height: 100vh;
    overflow: auto;
`;
const LoginContainer = styled(createBox({
    maxWidth: 'calc(100vw - 48px)',
    position: 'relative',
    width: '380px',
    marginTop: {
        tabletLandscape: '0',
    },
})).withConfig({
    shouldForwardProp: prop => prop !== 'whiteLabelConfiguration',
}) `
    /* Drop shadow around and underneath PIN */
    ${PINButtonContainer} {
        overflow: hidden;
        border-radius: 16px;
    }
`;
// Tighter on phones, where the logo, the site and the whole keypad have to fit on one screen
const BodyContainer = createStack({ mt: { base: 2, tabletPortrait: 5 }, pb: { base: 4, tabletPortrait: 11 } });
const StyledButtonsContainer = styled(createStack({
    // The same on both sides of the corner, so the menu sits as far from the top as from the edge
    px: { base: '16px', tabletPortrait: '40px' },
    pt: { base: '16px', tabletPortrait: '40px' },
})) `
    ${Breakpoints.tabletLandscapeAndLarger `
        box-sizing: border-box;
        position: absolute;
        width: 100%;
    `}
`;
const makeProgressPillCSS = (configuration) => css `
    ${ProgressPill} {
        box-shadow: 0 0 0 2px ${configuration.PinShadowColor};

        &:after {
            background: ${configuration.PinPillColor};
        }
    }
`;
const StyledProgressPills = styled(ProgressPills).withConfig({
    shouldForwardProp: prop => prop !== 'whiteLabelConfiguration',
}) `
    ${props => makeProgressPillCSS(props.whiteLabelConfiguration)}

    text-align: left;
    padding: ${props => props.theme.mf.spacing(4, 0, 3, 0)};
`;
const PINandQRContainer = createStack({
    flex: 1,
    marginTop: {
        base: 4,
        tabletPortrait: 12,
    },
    maxWidth: '100vw',
    overflow: 'auto hidden',
    flexDirection: {
        base: 'column',
        tabletLandscape: 'row',
        mobileLandscape: 'row',
        laptop: 'row',
    },
    paddingBottom: {
        base: '16px',
        tabletPortrait: '60px',
        mobileLandscape: 0,
    },
});
const QRContainer = createStack({
    mt: {
        base: 8,
        tabletLandscape: 0,
        mobileLandscape: 0,
        laptop: 0,
    },
});
const PinTextContainer = styled(TextContainer) `
    margin-bottom: 0;
`;
// We're stacking the logo and name horizontally on some tablets placed horizontally, to avoid scroll. 780px was a number agreed on with the design.
const NurseryLogoNameContainer = styled(createStack({
    flex: 1,
    alignItems: 'center',
    gap: '12px',
})) `
    flex-direction: column !important;
    max-width: 100%;

    h4 {
        word-break: break-word;
        padding: ${({ theme }) => theme.mf.spacing(2, 0)};
        text-align: center;
    }

    @media (max-height: 780px) and (min-width: 740px) {
        flex-direction: row !important;

        & > div:first-child {
            width: 40px;
            height: 40px;
        }

        h2 {
            font-size: 32px;
        }

        h4 {
            text-align: left;
        }
    }
`;
const StyledLogoContainer = styled(createStack({})).withConfig({
    shouldForwardProp: prop => prop !== 'customBorderColor',
}) `
    border: 4px solid ${props => props.customBorderColor};
    flex-shrink: 0;

    @media (max-height: 780px) and (min-width: 740px) {
        min-width: 40px;
        min-height: 40px;
        width: 40px !important;
        height: 40px !important;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;
const PIN_SIZE = 4;
const Pin = () => {
    const token = useTypedSelector(accessToken);
    const title = useTypedSelector(institutionTitle);
    const url = useTypedSelector(redirectUrl);
    const siteId = useTypedSelector(getSiteId);
    const institutionLoaded = useTypedSelector(didLoadInstitution);
    const siteImage = useTypedSelector(state => state.institutionOverview.institution && state.institutionOverview.institution.image.large);
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const p400 = useCustomWhitelabelColor('p400');
    const isQRSigninEnabled = useTypedSelector(isQrSignin);
    const [siteIdFromNetwork, setSiteIdFromNetwork] = React.useState('');
    // Initials for the institution title (max 2 letters - if two word name)
    const initials = React.useMemo(() => {
        if (title) {
            const words = title.split(' ');
            if (words.length > 1) {
                return words[0][0] + words[1][0];
            }
            return words[0][0];
        }
        return '';
    }, [title]);
    const dispatch = useDispatch();
    /** Allows to show the QR code immediately after it is enabled from settings, without the need to log out */
    const [qrEnabledFromNetwork, { loading }] = useTransformQuery()(LoginQueries.GetQRSignInSettings, { ...(siteId ? { variables: { siteIds: [siteId] }, fetchPolicy: 'network-only' } : { fetchPolicy: 'network-only' }) }, ({ data }) => {
        var _a, _b;
        if (data && ((_a = data.qrSignin.siteSettings.siteSettings[0]) === null || _a === void 0 ? void 0 : _a.siteId) && !siteId && !siteIdFromNetwork) {
            /** When switching from v1 to v2 without logging out, the siteId is not there in local storage (it's only saved when logging in to v2).
             * And because it is needed for the QR component, we're storing it additionally in state for when it's fetched */
            saveSiteId(data.qrSignin.siteSettings.siteSettings[0].siteId);
            setSiteIdFromNetwork(data.qrSignin.siteSettings.siteSettings[0].siteId);
        }
        return (_b = data === null || data === void 0 ? void 0 : data.qrSignin.siteSettings.siteSettings[0]) === null || _b === void 0 ? void 0 : _b.isEnabled;
    });
    React.useEffect(() => {
        if (token && !institutionLoaded) {
            fetchOverview.dispatch(token);
        }
    }, [institutionLoaded, token]);
    React.useEffect(() => {
        if (!loading && hasValue(qrEnabledFromNetwork) && isQRSigninEnabled !== qrEnabledFromNetwork) {
            saveIsQRSigninAvailable(Boolean(qrEnabledFromNetwork));
            dispatch(LoginActions.qrSigninChange.action(Boolean(qrEnabledFromNetwork)));
        }
    }, [dispatch, isQRSigninEnabled, loading, qrEnabledFromNetwork]);
    const onPinFinish = React.useCallback((pinValue) => {
        pinLogin.dispatch(pinValue);
    }, []);
    const { data: deviceNameData } = useResilientQuery(Queries.GetDeviceName, { fetchPolicy: 'network-only' });
    // Listen for biometric events to unlock signin app (if enabled)
    const { isSubscribed, enabled: doorLockingEnabled } = useListenForBiometricEvents(onPinFinish);
    const { showModal: showInfoModal, hideModal: hideInfoModal } = useModal({
        lazyChildren: loadInfoModal,
        size: 'compact',
    });
    if (url) {
        return _jsx(Redirect, { push: true, to: url });
    }
    const additionalOptions = [
        {
            label: translate('deviceStatus'),
            onClick: () => {
                var _a;
                showInfoModal({
                    handleClose: () => hideInfoModal(),
                    device: (_a = deviceNameData === null || deviceNameData === void 0 ? void 0 : deviceNameData.me) === null || _a === void 0 ? void 0 : _a.deviceInformation,
                    doorlockingSubscriptionConnectionStatus: isSubscribed,
                    doorlockingEnabled: doorLockingEnabled,
                });
            },
        },
    ];
    return (_jsxs(StyledContainer, { flexDirection: "column", className: "container", minHeight: { base: 'none', tabletLandscape: isQRSigninEnabled ? 'none' : '100vh' }, children: [_jsxs(StyledButtonsContainer, { justifyContent: "space-between", children: [_jsx(HeaderLogoContainer, { children: _jsx(WhitelabelLogo, { url: whitelabelConfiguration.PinLogo, height: whitelabelConfiguration.PinLogoHeight }) }), _jsxs(Stack, { alignItems: "center", alignSelf: "flex-start", gap: 4, children: [_jsx(GuestEntry, { returnTo: RoutesMap.pin }), _jsx(SettingsDropdown, { showWarning: true, siteName: title, variant: "light", settings: additionalOptions })] })] }), _jsxs(BodyContainer, { flex: 1, flexDirection: { base: 'column', tabletLandscape: isQRSigninEnabled ? 'column' : 'row' }, alignItems: "center", justifyContent: { base: 'center', tabletLandscape: 'space-between' }, maxWidth: "800px", width: "100%", mx: "auto", color: whitelabelConfiguration.LoginPagesFontColor, children: [_jsxs(NurseryLogoNameContainer, { children: [_jsx(StyledLogoContainer, { height: { base: '72px', tabletPortrait: '128px' }, width: { base: '72px', tabletPortrait: '128px' }, borderRadius: "50%", overflow: "hidden", customBorderColor: p400, children: whitelabelConfiguration.WithSiteImage && siteImage ? (_jsx("img", { src: siteImage, width: 128, height: 128 })) : (_jsx(Initials, { initials: initials })) }), _jsx(Text, { variant: "h4", emphasized: true, children: title })] }), _jsxs(PINandQRContainer, { justifyContent: isQRSigninEnabled ? 'space-between' : 'center', width: isQRSigninEnabled
                            ? {
                                tabletLandscape: '864px',
                            }
                            : undefined, children: [_jsxs(Stack, { flexDirection: "column", alignItems: "center", "data-e2e-id": "pin-container", children: [_jsx(PinTextContainer, { children: _jsx(Text, { variant: "h6", marginBottom: "10px", children: translate('enterPinCode') }) }), _jsx(PerformantProgressPills, {}), _jsx(LoginContainer, { whiteLabelConfiguration: whitelabelConfiguration, children: _jsx(PerformantPIN, { handleFinish: onPinFinish }) })] }), isQRSigninEnabled ? (_jsxs(QRContainer, { flexDirection: "column", alignItems: "center", "data-e2e-id": "qr-container", children: [_jsx(TextContainer, { children: _jsx(Text, { variant: "h5", marginBottom: "10px", children: translate('qrCodeExplanation') }) }), _jsx(QRCode, { institutionId: siteId ? siteId : siteIdFromNetwork })] })) : null] })] })] }));
};
export default Pin;
const PerformantProgressPills = () => {
    const length = useTypedSelector(pinHolder).length;
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const pinLoadingState = useTypedSelector(pinState);
    const pillState = getPillState(pinLoadingState);
    return (_jsx(StyledProgressPills, { whiteLabelConfiguration: whitelabelConfiguration, count: PIN_SIZE, appearance: "Colorful", activeCount: length, pillState: pillState }));
};
const PerformantPIN = ({ handleFinish }) => {
    const pinValue = useTypedSelector(pinHolder);
    const handleChangePin = useBindDispatch(setPinValue);
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const pinLoadingState = useTypedSelector(pinState);
    React.useEffect(() => {
        if (pinLoadingState === 'Error') {
            handleChangePin();
        }
    }, [handleChangePin, pinLoadingState]);
    return (_jsx("div", { children: _jsx(StyledPIN, { value: pinValue, pinSize: PIN_SIZE, onChange: handleChangePin, onFinish: handleFinish, whiteLabelConfig: whitelabelConfiguration, withPills: false }) }));
};
const StyledPIN = styled(PIN) `
    ${StyledPINButton} {
        border: none;
        color: ${props => props.theme.mf.colorPalette[props.whiteLabelConfig.PinButtonColor]};
        font-size: 48px;
        font-weight: 400;
        position: relative;
        padding: 16px 24px;
        font-family: ${props => props.theme.fontConfiguration.family} !important;
        justify-content: end;

        &,
        &&&[disabled],
        &:hover:not([disabled]) {
            opacity: 1;
            background-color: transparent;
        }

        &:nth-child(-n + 3) {
            margin-top: 16px;
        }

        &:nth-child(-n + 9)::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 64px;
            height: 1px;
            background: ${props => props.theme.mf.colorPalette[props.whiteLabelConfig.PinButtonColor]};
            opacity: 0.5;
        }

        &:nth-child(3n + 1),
        &:nth-child(3n + 2) {
            border-right: 1px solid ${props => props.theme.mf.colorPalette[props.whiteLabelConfig.PinButtonColor]};
        }
    }
    ${StyledBackspace} {
        height: 71px;
        width: 71px;
        g {
            fill: ${props => props.theme.mf.colorPalette[props.whiteLabelConfig.PinButtonColor]};
        }
    }

    ${PINButtonContainer} {
        // Unified height for all buttons (80px) + top padding (16px)
        grid-template-rows: repeat(4, 96px);
    }

    ${media.mobileSideways `
        ${StyledPINButton} {
            // Overrides parent font-size
            font-size: 48px !important;
        }

        
        ${PINButtonContainer} {
            // Unified height for all buttons (80px) + top padding (16px)
            grid-template-rows: repeat(4, 96px);
        }
    `}

    ${media.mobile `
        ${PINButtonContainer} {
            // Unified height for all buttons, but no more than a tenth of the screen so the whole keypad fits on a phone
            grid-template-rows: repeat(4, min(96px, 10vh));
        }

        ${StyledPINButton} {
            font-size: 48px;
        }
    `}

    ${props => makeProgressPillCSS(props.whiteLabelConfig)}
`;
